import { DataPartState, DataStoreState } from './types';
import { OmitStrict } from '../../types';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import lodashSet from 'lodash/set';
import lodashGet from 'lodash/get';
import lodashIsEqual from 'lodash/isEqual';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const identityFn = <TData,>(data: TData): TData => data;

const doesMapHaveAnyValueThatMatchesFlag = <TData,>(
  source: Record<string, TData>,
  extractorFn: (data: TData) => boolean | null,
  flag: boolean,
) => {
  const keys = Object.keys(source);
  return keys.length === 0
    ? null
    : keys.some(
        (a) => extractorFn(source[a]) === undefined || source[a] === null,
      )
    ? null
    : keys.some((a) => extractorFn(source[a]) === flag);
};

export const DataStoreContext = createContext<DataStoreState | undefined>(
  undefined,
);

export const useDataStore = (): DataStoreState => {
  const context = useContext(DataStoreContext);
  if (!context) {
    throw Error(`Could not find DataStoreContext in the component hierarchy.`);
  }
  return context;
};
export const DataStoreProvider = DataStoreContext.Provider;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DataStoreProps<TData extends Record<string, any>> = {
  initialData?: TData;
  buildKey: (key: string[]) => string;
  extractKeys: (input: string) => string[];
  children?: (props: DataStoreState) => ReactNode;
};

type DataPartLocalState = OmitStrict<DataPartState<unknown>, 'data'>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function DataStore<TData extends Record<string, any>>(
  props: DataStoreProps<TData>,
) {
  const { children, initialData, buildKey, extractKeys } = props;

  const [data, setData] = useState<TData>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initialData || ({} as any),
  );

  const [dataPartsLocalStates, setDataPartsLocalStates] = useState<
    Record<string, DataPartLocalState>
  >({});

  const isDirty = useMemo(() => {
    const hasAnyDirty = doesMapHaveAnyValueThatMatchesFlag(
      dataPartsLocalStates,
      (a) => a.isDirty,
      true,
    );
    return hasAnyDirty === null ? hasAnyDirty : hasAnyDirty === true;
  }, [dataPartsLocalStates]);

  const isValid = useMemo(() => {
    const hasAnyInvalid = doesMapHaveAnyValueThatMatchesFlag(
      dataPartsLocalStates,
      (a) => a.isValid,
      false,
    );
    return hasAnyInvalid === null ? hasAnyInvalid : hasAnyInvalid === false;
  }, [dataPartsLocalStates]);

  const isValidAsDraft = useMemo(() => {
    const hasAnyInvalidAsDraft = doesMapHaveAnyValueThatMatchesFlag(
      dataPartsLocalStates,
      (a) => a.isValidAsDraft,
      false,
    );

    return hasAnyInvalidAsDraft === null
      ? hasAnyInvalidAsDraft
      : hasAnyInvalidAsDraft === false;
  }, [dataPartsLocalStates]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setData(initialData || ({} as any));
  }, [initialData]);

  const updateDataPartState = useCallback(
    <TData,>(key: string[], dataPartState: DataPartState<TData>) => {
      const {
        isDirty,
        isValid,
        isValidAsDraft,
        data: dataPartData,
      } = dataPartState;
      const concatenatedKey = buildKey(key);
      setDataPartsLocalStates((currentState) => {
        const newDataPartLocalState = {
          isDirty,
          isValid,
          isValidAsDraft,
        };
        const current = currentState[concatenatedKey] || {};
        if (!lodashIsEqual(current, newDataPartLocalState)) {
          return {
            ...currentState,
            [concatenatedKey]: newDataPartLocalState,
          };
        }
        return currentState;
      });

      setData((currentData) => {
        const currentDataPart = lodashGet(currentData, key);
        if (!lodashIsEqual(currentDataPart, dataPartData)) {
          return lodashSet({ ...currentData }, key, dataPartData);
        }
        return currentData;
      });
    },
    [buildKey],
  );
  const getDataPartState = useCallback(
    <TDataPart,>(key: string[]): DataPartState<TDataPart> => {
      const concatenatedKey = buildKey(key);
      const dataPartLocalState = dataPartsLocalStates[concatenatedKey];
      const dataPartData = lodashGet(data, key);
      const toReturn: DataPartState<TDataPart> = {
        ...dataPartLocalState,
        data: dataPartData,
      };
      return toReturn;
    },
    [buildKey, data, dataPartsLocalStates],
  );
  const value: DataStoreState = useMemo(() => {
    return {
      isDirty,
      buildKey,
      extractKeys,
      isValid,
      isValidAsDraft,
      data,
      updateDataPartState,
      getDataPartState,
    };
  }, [
    buildKey,
    data,
    extractKeys,
    getDataPartState,
    isDirty,
    isValid,
    isValidAsDraft,
    updateDataPartState,
  ]);
  return (
    <DataStoreProvider value={value}>
      {children && children(value)}
    </DataStoreProvider>
  );
}
